/**
 * `SearchableSelect`'s attendees-first view (#15938), kept beside it because
 * `searchable-select.tsx` sits just under the 1000-line
 * `check:file-size-ratchet` cap.
 *
 * The ordering is `rankAttendeesFirst` — the same helper
 * `TaskAssigneeDropdown` uses — and the labelling rides the select's
 * existing grouped mode (#13473): this only supplies a `getGroup`, so the
 * flat index space, option ids and arrow-key walk are untouched.
 */

import {
  ATTENDEE_GROUP_LABEL,
  isAttendeeFirstQueryEmpty,
  NON_ATTENDEE_GROUP_LABEL,
  rankAttendeesFirst,
} from '@/lib/meetings/attendee-first-ranking'

export interface AttendeeFirstView<T> {
  /** The rows to render, already narrowed and ranked. */
  rows: T[]
  /** Labels each row "Attendees" or "Everyone else". */
  getGroup: (item: T) => string
  /** True under an empty query, when only attendees are listed. */
  showHint: boolean
}

export function attendeeFirstView<T>(
  items: readonly T[],
  attendeeIds: readonly string[],
  getId: (item: T) => string,
  getSearchText: (item: T) => string,
  query: string,
): AttendeeFirstView<T> {
  const attendees = new Set(attendeeIds)
  return {
    rows: rankAttendeesFirst(items, {
      getId,
      getSearchText,
      attendeeIds: attendees,
      query,
    }),
    getGroup: item =>
      attendees.has(getId(item))
        ? ATTENDEE_GROUP_LABEL
        : NON_ATTENDEE_GROUP_LABEL,
    showHint: isAttendeeFirstQueryEmpty(query),
  }
}
